import type { Scenario, TestCase, ReconciliationSession } from '../types'
import { apiFetch, getAuthHeader, handleUnauthorized } from './core'

export async function getScenarios(): Promise<Scenario[]> {
  return apiFetch<Scenario[]>('/api/testcases/scenarios')
}

export async function createScenario(data: { name: string; description?: string }): Promise<Scenario> {
  return apiFetch<Scenario>('/api/testcases/scenarios', { method: 'POST', body: JSON.stringify(data) })
}

export async function updateScenario(id: number, data: { name: string; description?: string }): Promise<Scenario> {
  return apiFetch<Scenario>(`/api/testcases/scenarios/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export async function deleteScenario(id: number): Promise<void> {
  return apiFetch<void>(`/api/testcases/scenarios/${id}`, { method: 'DELETE' })
}

export async function createTestCase(
  scenarioId: number,
  name: string,
  description: string,
  files: File[],
): Promise<TestCase> {
  const form = new FormData()
  form.append('name', name)
  form.append('description', description)
  files.forEach((f) => form.append('files', f))
  const res = await fetch(`/api/testcases/scenarios/${scenarioId}/cases`, {
    method: 'POST',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export async function updateTestCase(id: number, data: { name: string; description?: string }): Promise<TestCase> {
  return apiFetch<TestCase>(`/api/testcases/cases/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

export async function replaceTestCaseFiles(id: number, files: File[]): Promise<TestCase> {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  const res = await fetch(`/api/testcases/cases/${id}/files`, {
    method: 'PUT',
    headers: { Authorization: getAuthHeader() },
    body: form,
  })
  handleUnauthorized(res)
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(body.detail ?? res.statusText)
  }
  return res.json()
}

export async function deleteTestCase(id: number): Promise<void> {
  return apiFetch<void>(`/api/testcases/cases/${id}`, { method: 'DELETE' })
}

export async function runTestCase(id: number): Promise<ReconciliationSession> {
  return apiFetch<ReconciliationSession>(`/api/testcases/cases/${id}/run`, { method: 'POST' })
}

export async function saveSessionAsTestCase(
  scenarioId: number,
  sessionId: number,
  name: string,
  description?: string,
): Promise<TestCase> {
  return apiFetch<TestCase>(
    `/api/testcases/scenarios/${scenarioId}/cases/from-session/${sessionId}`,
    { method: 'POST', body: JSON.stringify({ name, description: description ?? null }) },
  )
}
